import { useEffect, useMemo, useState } from "react";
import {
    X, ShieldCheck, Database, ChevronDown, Clock, Activity,
} from "lucide-react";
import { Confidence } from "./Severity";

/**
 * EvidenceDrawer — accounting-grade provenance.
 *
 * Header: "Why you can trust this answer" with plain-English
 *           data mode / freshness / confidence / sources / record count.
 * Body:   labelled sections, no developer jargon.
 * Footer: Advanced provenance (model, generated_at, raw ids) behind disclosure.
 */

const KIND_LABEL = {
    metric_table: "Key financial metrics",
    confidence_summary: "Data confidence",
    changes: "Recent movements",
    audit_findings: "Audit findings",
    cash_reality: "Cash reality",
    forecast: "Forecast",
    pattern: "Detected patterns",
    note: "Management notes",
};

const KIND_ICON = {
    metric_table: Database,
    confidence_summary: ShieldCheck,
    changes: Activity,
    audit_findings: ShieldCheck,
};

function freshnessLabel(generatedAt) {
    if (!generatedAt) return "—";
    const ms = Date.now() - new Date(generatedAt).getTime();
    const mins = Math.round(ms / 60000);
    if (mins < 2) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
    const days = Math.round(hrs / 24);
    return `${days} day${days > 1 ? "s" : ""} ago`;
}

const DATA_MODE_LABEL = {
    production_live: "Live MYOB data",
    production_pending: "Connecting",
    production_error: "Needs attention",
    mock: "Demo data",
};

const BASIS_LABEL = {
    truth_layer: "Truth layer (entity snapshots)",
    audit_findings: "Audit findings",
    what_changed: "Recent change log",
    reconciliation: "Reconciliation engine",
    cash_reality: "Cash reality engine",
    forecast: "13-week forecast",
    patterns: "Pattern signature engine",
    notes: "Management notes",
    financial_snapshot: "Financial snapshots",
    benchmarks: "Benchmark comparisons",
};

function humaniseBasis(tag) {
    if (BASIS_LABEL[tag]) return BASIS_LABEL[tag];
    // Friendlify e.g. "sales_pipeline" → "Sales pipeline"
    const s = String(tag).replace(/_/g, " ").trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function EvidenceBlockRow({ block }) {
    const Icon = KIND_ICON[block.kind] || Database;
    const label = block.title || KIND_LABEL[block.kind] || "Supporting data";
    // Normalise rows into clean key/value pairs
    const rows = (block.rows || []).slice(0, 12);
    return (
        <div className="bg-stone-50 rounded-lg px-4 py-3.5">
            <div className="flex items-center gap-2 mb-2">
                <Icon size={12} strokeWidth={2} className="text-stone-500" />
                <div className="text-[13px] font-medium text-stone-800">{label}</div>
            </div>
            {rows.length > 0 ? (
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {rows.map((r, i) => (
                        <div key={i} className="contents">
                            <dt className="text-[12px] text-stone-500 truncate">
                                {r.label}
                            </dt>
                            <dd className="text-[12px] text-stone-800 font-mono-num text-right">
                                {r.value === null || r.value === undefined ? "—" : String(r.value)}
                            </dd>
                        </div>
                    ))}
                </dl>
            ) : block.summary ? (
                <div className="text-[12px] text-stone-700 leading-relaxed">
                    {block.summary}
                </div>
            ) : null}
        </div>
    );
}

export function EvidenceDrawer({
    open,
    onClose,
    title = "Evidence",
    confidence,
    dataBasis = [],
    evidenceBlocks = [],
    evidenceRefs = [],
    generatedAt,
    model,
    dataMode,
    children,
}) {
    const [advancedOpen, setAdvancedOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && onClose?.();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const sourceLabels = useMemo(
        () => dataBasis.map(humaniseBasis),
        [dataBasis],
    );

    return (
        <>
            <div
                className={`fixed inset-0 bg-stone-900/30 backdrop-blur-sm z-40 transition-opacity duration-200 ${
                    open ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
                onClick={onClose}
                aria-hidden="true"
            />
            <aside
                className={`fixed top-0 right-0 h-screen w-full max-w-[460px] bg-white border-l border-stone-100 z-50 transform transition-transform duration-250 ease-out flex flex-col ${
                    open ? "translate-x-0" : "translate-x-full"
                }`}
                role="dialog"
                aria-label="Evidence drawer"
                data-testid="evidence-drawer"
            >
                <header className="flex items-start justify-between px-7 pt-6 pb-5 border-b border-stone-100">
                    <div className="min-w-0 flex-1">
                        <div className="text-[11px] uppercase tracking-wider text-stone-500 font-medium">
                            Evidence
                        </div>
                        <h2 className="text-[18px] font-medium text-stone-900 mt-1 leading-snug">
                            Why you can trust this answer
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 -mr-1.5 -mt-1 rounded hover:bg-stone-100 text-stone-500"
                        aria-label="Close evidence drawer"
                        data-testid="evidence-drawer-close"
                    >
                        <X size={15} strokeWidth={2} />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto thin-scroll px-7 py-6 space-y-7">
                    {/* Trust summary */}
                    <section
                        className="grid grid-cols-2 gap-x-4 gap-y-3"
                        data-testid="evidence-trust-summary"
                    >
                        <Field
                            label="Data mode"
                            value={DATA_MODE_LABEL[dataMode] || dataMode || "—"}
                        />
                        <Field
                            label="Confidence"
                            value={confidence ? <Confidence level={confidence} /> : "—"}
                        />
                        <Field
                            label="Freshness"
                            value={freshnessLabel(generatedAt)}
                        />
                        <Field
                            label="Supporting records"
                            value={`${evidenceRefs.length} cited · ${evidenceBlocks.length} blocks`}
                        />
                    </section>

                    {/* Source systems */}
                    {sourceLabels.length > 0 && (
                        <section>
                            <div className="text-[12px] uppercase tracking-wider text-stone-500 font-medium mb-2">
                                Source systems
                            </div>
                            <ul className="space-y-1.5">
                                {sourceLabels.map((s, i) => (
                                    <li
                                        key={i}
                                        className="text-[13px] text-stone-700 leading-snug flex items-start gap-2"
                                    >
                                        <span className="text-stone-400 mt-1.5 w-1 h-1 rounded-full bg-stone-300 flex-shrink-0" />
                                        {s}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* Evidence blocks */}
                    {evidenceBlocks.length > 0 && (
                        <section>
                            <div className="text-[12px] uppercase tracking-wider text-stone-500 font-medium mb-2.5">
                                Supporting figures
                            </div>
                            <div className="space-y-2.5">
                                {evidenceBlocks.map((b, i) => (
                                    <EvidenceBlockRow key={i} block={b} />
                                ))}
                            </div>
                        </section>
                    )}

                    {children}

                    {/* Advanced provenance — collapsed by default */}
                    <section className="pt-3 border-t border-stone-100">
                        <button
                            type="button"
                            onClick={() => setAdvancedOpen((s) => !s)}
                            className="w-full flex items-center justify-between text-[12px] text-stone-500 hover:text-stone-700"
                            data-testid="evidence-advanced-toggle"
                        >
                            <span className="uppercase tracking-wider font-medium">
                                Advanced provenance
                            </span>
                            <ChevronDown
                                size={12}
                                strokeWidth={2.5}
                                className={`transition-transform ${
                                    advancedOpen ? "rotate-0" : "-rotate-90"
                                }`}
                            />
                        </button>
                        {advancedOpen && (
                            <div className="mt-3 space-y-3 text-[12px] text-stone-600">
                                {generatedAt && (
                                    <Field
                                        label="Generated"
                                        value={
                                            <span className="font-mono text-[11px]">
                                                {new Date(generatedAt).toLocaleString(
                                                    "en-AU",
                                                    {
                                                        day: "2-digit",
                                                        month: "short",
                                                        year: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    },
                                                )}
                                            </span>
                                        }
                                    />
                                )}
                                {model && <Field label="Model" value={<span className="font-mono text-[11px]">{model}</span>} />}
                                {evidenceRefs.length > 0 && (
                                    <div>
                                        <div className="label-soft text-stone-500 mb-1">
                                            Record references
                                        </div>
                                        <ul className="font-mono text-[11px] text-stone-500 space-y-0.5 break-all">
                                            {evidenceRefs.slice(0, 30).map((r, i) => (
                                                <li key={i}>{r}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>

                    {evidenceBlocks.length === 0 && evidenceRefs.length === 0 && (
                        <div className="text-[13px] text-stone-500">
                            No supporting figures attached to this response.
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="label-soft text-stone-500">{label}</div>
            <div className="text-[13px] text-stone-800 mt-0.5">{value}</div>
        </div>
    );
}
